import { useState } from "react";
import { useGameAuth } from "@/context/GameAuthContext";
import { gameApi, formatApiErrorDetail } from "@/lib/api";
import { Sparkles, MailCheck, ArrowLeft } from "lucide-react";

export const GameAuth = () => {
  const { login, register } = useGameAuth();
  const [mode, setMode] = useState("register"); // register | login | forgot
  const [form, setForm] = useState({ email: "", password: "", display_name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const go = (m) => { setErr(""); setSent(false); setMode(m); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "register") await register(form.email, form.password, form.display_name);
      else if (mode === "login") await login(form.email, form.password);
      else {
        await gameApi.post("/game/auth/forgot-password", { email: form.email });
        setSent(true);
      }
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const title = mode === "register" ? "Create your player" : mode === "login" ? "Welcome back" : "Reset your password";

  return (
    <div className="px-4 pt-6" data-testid="game-auth">
      <div className="mx-auto max-w-sm rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="mb-4 text-center">
          <span className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-full bg-secondary/15 text-secondary">
            <Sparkles size={22} />
          </span>
          <h2 className="font-display text-2xl font-bold text-primary">{title}</h2>
          <p className="text-xs text-muted-foreground">
            {mode === "forgot" ? "We'll email you a secure link to set a new password." : "Your game account is separate from the event site."}
          </p>
        </div>

        {mode === "forgot" && sent ? (
          <div data-testid="forgot-sent" className="text-center">
            <span className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-emerald-600"><MailCheck size={22} /></span>
            <p className="text-sm text-foreground/80">If an account exists for <span className="font-bold text-primary">{form.email}</span>, we've sent a password reset link. Check your inbox (and spam folder).</p>
            <button data-testid="forgot-back-login" onClick={() => go("login")}
              className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-secondary"><ArrowLeft size={14} /> Back to log in</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <input data-testid="game-display-name" required value={form.display_name} onChange={set("display_name")}
                placeholder="Display name" maxLength={30}
                className="h-12 w-full rounded-xl border border-border bg-background px-4 outline-none focus:ring-2 focus:ring-secondary" />
            )}
            <input data-testid="game-email" type="email" required value={form.email} onChange={set("email")}
              placeholder="Email" autoComplete="email"
              className="h-12 w-full rounded-xl border border-border bg-background px-4 outline-none focus:ring-2 focus:ring-secondary" />
            {mode !== "forgot" && (
              <input data-testid="game-password" type="password" required value={form.password} onChange={set("password")}
                placeholder="Password (min 6 characters)" autoComplete={mode === "register" ? "new-password" : "current-password"}
                className="h-12 w-full rounded-xl border border-border bg-background px-4 outline-none focus:ring-2 focus:ring-secondary" />
            )}
            {mode === "login" && (
              <button type="button" data-testid="game-forgot-link" onClick={() => go("forgot")}
                className="block w-full text-right text-xs font-semibold text-secondary">Forgot password?</button>
            )}
            {err && <p data-testid="game-auth-error" className="text-sm font-semibold text-destructive">{err}</p>}
            <button data-testid="game-auth-submit" disabled={busy} type="submit"
              className="h-12 w-full rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">
              {busy ? "Please wait…" : mode === "register" ? "Start Playing" : mode === "login" ? "Log In" : "Send Reset Link"}
            </button>
          </form>
        )}

        {mode === "forgot" ? (
          !sent && (
            <button data-testid="forgot-cancel" onClick={() => go("login")}
              className="mt-3 inline-flex w-full items-center justify-center gap-1 text-center text-sm font-semibold text-secondary">
              <ArrowLeft size={14} /> Back to log in
            </button>
          )
        ) : (
          <button data-testid="game-auth-toggle" onClick={() => go(mode === "register" ? "login" : "register")}
            className="mt-3 w-full text-center text-sm font-semibold text-secondary">
            {mode === "register" ? "Already have a player? Log in" : "New here? Create a player"}
          </button>
        )}
      </div>
    </div>
  );
};
